import React, { useEffect, useState } from 'react';
import { useBooking } from '../../contexts/booking/BookingContext';
import { Link, useSearchParams } from 'react-router-dom';
import { format, parseISO, addMinutes } from 'date-fns';
import { getTotalDuration } from '../../utils/bookingUtils';
import { supabase, SUPABASE_ANON_KEY } from '../../lib/supabaseClient';
import { edgeFunctionUrl, EdgeFunctions } from '../../lib/edgeFunctions';

/**
 * Component displayed after a successful booking
 */
const BookingConfirmation: React.FC = () => {
  const { bookingState, resetBooking, setPaymentIntent, setPaymentStatus, clearBookingState } = useBooking();
  const { selectedServices, selectedStaff, selectedDate, selectedTime, customerInfo } = bookingState;
  const [searchParams] = useSearchParams();
  const [isConfirmingPayment, setIsConfirmingPayment] = useState(false);
  
  // Clear localStorage on mount (successful booking completed)
  useEffect(() => {
    clearBookingState();
    console.log('🧹 Cleared booking state from localStorage');
  }, [clearBookingState]);
  
  // Handle Afterpay redirect back
  useEffect(() => {
    const handleAfterPayReturn = async () => {
      const paymentIntentId = searchParams.get('payment_intent');
      const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');
      const appointmentId = searchParams.get('appointment_id');
      
      // If we have these params, user is returning from Afterpay
      if (paymentIntentId && paymentIntentClientSecret && appointmentId) {
        setIsConfirmingPayment(true);
        
        try {
          console.log('Confirming Afterpay payment:', paymentIntentId);
          
          // Update booking context
          setPaymentIntent(paymentIntentId);
          setPaymentStatus('succeeded');
          
          // Confirm payment on backend
          const { data: { session } } = await supabase.auth.getSession();
          const accessToken = session?.access_token;

          const confirmHeaders: Record<string, string> = {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
          };

          if (accessToken) {
            confirmHeaders['Authorization'] = `Bearer ${accessToken}`;
          }

          const confirmResponse = await fetch(
            edgeFunctionUrl(EdgeFunctions.CONFIRM_PAYMENT),
            {
              method: 'POST',
              headers: confirmHeaders,
              body: JSON.stringify({
                payment_intent_id: paymentIntentId,
                appointment_id: appointmentId,
              }),
            }
          );

          if (!confirmResponse.ok) {
            console.warn('Payment confirmation failed. Webhook will handle status update.');
          }
          
          console.log('Afterpay payment confirmed successfully');
        } catch (error) {
          console.error('Error confirming Afterpay payment:', error);
        } finally {
          setIsConfirmingPayment(false);
        }
      }
    };
    
    handleAfterPayReturn();
  }, [searchParams, setPaymentIntent, setPaymentStatus]);
  
  // Validate required booking information
  if (!selectedDate || !selectedTime || !customerInfo) {
    return (
      <div className="text-center">
        <h2 className="text-2xl font-serif text-[#232347] mb-4">Something went wrong</h2>
        <p className="text-gray-600 mb-6">We couldn't find your booking details.</p>
        <Link 
          to="/" 
          className="inline-block px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors"
        >
          Return to Home
        </Link>
      </div>
    );
  }
  
  // Format the appointment time for display
  if (!selectedTime) {
    console.error('[BookingConfirmation] selectedTime is null or undefined. Cannot proceed.');
    return <div>Error: Booking time not available for confirmation.</div>;
  }
  
  const appointmentDateTime = parseISO(selectedTime);
  const totalDuration = getTotalDuration(selectedServices);
  const appointmentEndTime = addMinutes(appointmentDateTime, totalDuration);
  
  // Show loading state while confirming Afterpay payment
  if (isConfirmingPayment) {
    return (
      <div className="text-center py-12">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-[#B2D8C5] rounded-full mb-4">
          <div className="animate-spin h-8 w-8 border-4 border-white border-t-transparent rounded-full"></div>
        </div>
        <h2 className="text-2xl font-serif text-[#232347] mb-2">Confirming Payment...</h2>
        <p className="text-gray-600">Please wait while we confirm your Afterpay payment.</p>
      </div>
    );
  }
  
  return (
    <div className="text-center">
      <div className="mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-[#B2D8C5] rounded-full mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        
        <h2 className="text-2xl md:text-3xl font-serif text-[#232347] mb-2">Booking Confirmed!</h2>
        <p className="text-gray-600">
          Your appointment has been successfully scheduled and {bookingState.paymentStatus === 'succeeded' ? 'payment has been processed.' : 'payment is being processed.'}
        </p>
        
        {bookingState.paymentIntentId && (
          <div className="mt-4 text-sm bg-[#F1F7F4] p-2 rounded-md">
            <p className="text-gray-700">Payment Reference: {bookingState.paymentIntentId.substring(0, 14)}...</p>
          </div>
        )}
      </div>
      
      <div className="bg-[#F1F7F4] p-6 rounded-md mb-8 text-left max-w-md mx-auto">
        <h3 className="text-xl font-serif text-[#232347] mb-4 text-center">Appointment Details</h3>
        
        {/* Date & Time */}
        <div className="mb-4">
          <h4 className="font-medium text-[#232347] mb-1">Date & Time</h4>
          <p className="text-gray-700">
            {format(appointmentDateTime, 'EEEE, MMMM d, yyyy')}
            <br />
            {format(appointmentDateTime, 'h:mm a')} - {format(appointmentEndTime, 'h:mm a')}
          </p>
        </div>
        
        {/* Services */}
        <div className="mb-4">
          <h4 className="font-medium text-[#232347] mb-1">Services</h4>
          <ul className="list-disc list-inside text-gray-700">
            {selectedServices.map(service => (
              <li key={service.id}>{service.name}</li>
            ))}
          </ul>
        </div>
        
        {/* Staff */}
        <div className="mb-4">
          <h4 className="font-medium text-[#232347] mb-1">Therapist</h4>
          <p className="text-gray-700">
            {selectedStaff ? selectedStaff.name : 'Best available therapist'}
          </p>
        </div>
        
        {/* Location */}
        <div>
          <h4 className="font-medium text-[#232347] mb-1">Location</h4>
          <p className="text-gray-700">
            Healing Lotus Spa<br />
            123 Relaxation Avenue<br />
            Serenity, CA 90210
          </p>
        </div>
      </div>
      
      <div className="bg-white border border-gray-200 p-4 rounded-md mb-8 text-left max-w-md mx-auto">
        <h3 className="font-medium text-[#232347] mb-2">What's Next?</h3>
        <ul className="space-y-2 text-gray-600 text-sm">
          <li className="flex">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B2D8C5] mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <span>A confirmation email with all details has been sent to {customerInfo.email}</span>
          </li>
          <li className="flex">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B2D8C5] mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Please arrive 15 minutes before your appointment time</span>
          </li>
          <li className="flex">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#B2D8C5] mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>Need to cancel or reschedule? Please call us at (555) 123-4567 at least 24 hours in advance</span>
          </li>
        </ul>
      </div>
      
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <Link 
          to="/" 
          className="px-6 py-2 rounded-md border border-[#B2D8C5] text-[#232347] hover:bg-[#F1F7F4] transition-colors"
          onClick={resetBooking}
          aria-label="Return to Home"
        >
          Return to Home
        </Link>
        <Link 
          to="/services" 
          className="px-6 py-2 rounded-md text-white bg-[#B2D8C5] hover:bg-[#88bfa3] transition-colors"
          onClick={resetBooking}
          aria-label="Book Another Service"
        >
          Book Another Service
        </Link>
      </div>
    </div>
  );
};

export default BookingConfirmation;